import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Save, X, Plus, MapPin, Calendar, Briefcase, GraduationCap, Star,
    AlertCircle, Check, FileImage, Trash2, ArrowLeft, Loader, Search
} from 'lucide-react';
import api, { getWilayas, getCommunesOfWilaya } from '../utils/api';

const NewJobOffer = () => {
    const navigate = useNavigate();
    const { id: recruiterId } = useParams();
    const fileInputRef = useRef(null);

    useEffect(() => {
        document.title = 'Nouvelle Offre - Admin Tekhdem';
    }, []);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        required_skills: [],
        address: {
            line1: '',
            line2: '',
            zipCode: '',
            city: '',
            wilaya: ''
        },
        contractType: '',
        experienceLevel: '',
        educationLevel: '',
        image: null,
        startAt: '',
        endAt: '',
        isFeatured: false,
        tags: [],
        sector: ''
    });

    const [availableJobs, setAvailableJobs] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [availableSectors, setAvailableSectors] = useState([]);
    const [newTag, setNewTag] = useState('');
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // Job Title Autocomplete
    const [jobSearchTerm, setJobSearchTerm] = useState('');
    const [showJobDropdown, setShowJobDropdown] = useState(false);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);

    // Skills Autocomplete
    const [skillSearchTerm, setSkillSearchTerm] = useState('');
    const [showSkillDropdown, setShowSkillDropdown] = useState(false);
    const [filteredSkills, setFilteredSkills] = useState([]);

    // Location Autocomplete State
    const [wilayas, setWilayas] = useState([]);
    const [communes, setCommunes] = useState([]);
    const [showWilayaDropdown, setShowWilayaDropdown] = useState(false);

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const res = await api.get("/job/");
                setAvailableJobs(res.data);
            } catch (err) {
                console.error("Erreur jobs:", err);
            }
        };
        fetchJobs();

        const fetchSkills = async () => {
            try {
                const res = await api.get("/skill/");
                setAvailableSkills(res.data);
            } catch (err) {
                console.error("Erreur skills:", err);
            }
        };
        fetchSkills();

        const fetchSectors = async () => {
            try {
                const res = await api.get("/sector/");
                setAvailableSectors(res.data);
            } catch (err) {
                console.error("Erreur sectors:", err);
            }
        };
        fetchSectors();
    }, []);

    // Filter jobs based on search term
    useEffect(() => {
        if (jobSearchTerm.trim() && !selectedJob) {
            const filtered = availableJobs.filter(job => {
                const jobTitle = job.title || '';
                return jobTitle.toLowerCase().includes(jobSearchTerm.toLowerCase());
            });
            setFilteredJobs(filtered.slice(0, 10));
        } else {
            setFilteredJobs([]);
        }
    }, [jobSearchTerm, availableJobs, selectedJob]);

    // Filter skills based on search term
    useEffect(() => {
        if (skillSearchTerm.trim()) {
            const filtered = availableSkills.filter(skill => {
                const skillName = skill.name_fr || skill.name || '';
                return skillName.toLowerCase().includes(skillSearchTerm.toLowerCase()) &&
                    !formData.required_skills.includes(skill._id);
            });
            setFilteredSkills(filtered.slice(0, 10));
        } else {
            setFilteredSkills([]);
        }
    }, [skillSearchTerm, availableSkills, formData.required_skills]);

    const contractTypes = [
        { value: 'cdi', label: 'CDI - Contrat à Durée Indéterminée' },
        { value: 'cdd', label: 'CDD - Contrat à Durée Déterminée' },
        { value: 'freelance', label: 'Freelance' },
        { value: 'internship', label: 'Stage' }
    ];

    const experienceLevels = [
        { value: 1, label: 'Junior (0-2 ans)' },
        { value: 2, label: 'Mid-level (2-5 ans)' },
        { value: 3, label: 'Senior (5+ ans)' }
    ];

    const educationLevels = [
        { value: 'aucun', label: 'Aucun diplôme requis' },
        { value: 'Bac', label: 'Baccalauréat' },
        { value: 'Bac+2', label: 'Bac+2 (BTS, DUT)' },
        { value: 'Bac+3', label: 'Bac+3 (Licence)' },
        { value: 'Bac+5', label: 'Bac+5 (Master)' },
        { value: 'Doctorat', label: 'Doctorat' }
    ];

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === 'file') {
            handleImageUpload(files[0]);
        } else if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: value }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleSelectJob = (job) => {
        setSelectedJob(job);
        setJobSearchTerm(job.title);
        setFormData(prev => ({ ...prev, title: job._id, sector: '' }));
        setShowJobDropdown(false);
        if (errors.title) {
            setErrors(prev => ({ ...prev, title: undefined }));
        }
    };

    const handleJobSearchChange = (e) => {
        const value = e.target.value;
        setJobSearchTerm(value);
        setSelectedJob(null);
        setFormData(prev => ({ ...prev, title: value }));
        setShowJobDropdown(true);
    };

    const handleImageUpload = (file) => {
        if (file) {
            setFormData(prev => ({ ...prev, image: file }));
            const reader = new FileReader();
            reader.onload = (e) => setImagePreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0 && files[0].type.startsWith('image/')) {
            handleImageUpload(files[0]);
        }
    };

    const removeImage = () => {
        setFormData(prev => ({ ...prev, image: null }));
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleAddSkill = (skill) => {
        if (!formData.required_skills.includes(skill._id)) {
            setFormData(prev => ({
                ...prev,
                required_skills: [...prev.required_skills, skill._id]
            }));
        }
        setSkillSearchTerm('');
        setShowSkillDropdown(false);
    };

    const handleRemoveSkill = (skillId) => {
        setFormData(prev => ({
            ...prev,
            required_skills: prev.required_skills.filter(id => id !== skillId)
        }));
    };

    const getSkillName = (skillId) => {
        const skill = availableSkills.find(s => s._id === skillId);
        return skill ? (skill.name_fr || skill.name) : '';
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleWilayaChange = async (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, wilaya: value, city: '', zipCode: '' }
        }));
        setCommunes([]);

        if (value.length > 0) {
            try {
                const res = await getWilayas(value);
                setWilayas(res);
                setShowWilayaDropdown(true);
            } catch (error) {
                console.error("Error fetching wilayas:", error);
            }
        } else {
            setWilayas([]);
            setShowWilayaDropdown(false);
        }
    };

    const selectWilaya = async (wilaya) => {
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, wilaya: wilaya.name_fr || wilaya.name, city: '', zipCode: '' }
        }));
        setWilayas([]);
        setShowWilayaDropdown(false);

        try {
            const res = await getCommunesOfWilaya(wilaya.code);
            setCommunes(res);
        } catch (error) {
            console.error("Error fetching communes for wilaya:", error);
        }
    };

    const handleCitySelect = (e) => {
        const cityName = e.target.value;
        const selectedCommune = communes.find(c => c.name === cityName);

        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                city: cityName,
                zipCode: selectedCommune ? selectedCommune.postalCode : ''
            }
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title) newErrors.title = 'Le titre du poste est requis';

        if (!selectedJob && !formData.sector && jobSearchTerm.trim()) {
            newErrors.sector = 'Veuillez sélectionner un secteur pour ce nouveau poste';
        }

        if (!formData.description) newErrors.description = 'La description est requise';
        if (!formData.address.zipCode) newErrors['address.zipCode'] = 'Le code postal est requis';
        if (!formData.address.city) newErrors['address.city'] = 'La ville est requise';
        if (!formData.contractType) newErrors.contractType = 'Le type de contrat est requis';
        if (!formData.experienceLevel) newErrors.experienceLevel = 'Le niveau d\'expérience est requis';

        if (formData.startAt) {
            const startDate = new Date(formData.startAt);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (startDate < today) {
                newErrors.startAt = 'La date de début ne peut pas être dans le passé';
            }
        }
        if (formData.endAt && formData.startAt) {
            const startDate = new Date(formData.startAt);
            const endDate = new Date(formData.endAt);
            if (endDate <= startDate) {
                newErrors.endAt = 'La date de fin doit être après la date de début';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('contractType', formData.contractType);
            formDataToSend.append('experienceLevel', formData.experienceLevel);
            formDataToSend.append('educationLevel', formData.educationLevel);
            formDataToSend.append('startAt', formData.startAt);
            formDataToSend.append('endAt', formData.endAt);
            formDataToSend.append('isFeatured', formData.isFeatured);
            formDataToSend.append('address', JSON.stringify(formData.address));
            formDataToSend.append('required_skills', JSON.stringify(formData.required_skills));
            formDataToSend.append('tags', JSON.stringify(formData.tags));

            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }

            if (formData.sector) {
                formDataToSend.append('sector', formData.sector);
            }

            await api.post(`/admin/recruiters/${recruiterId}/job-offers`, formDataToSend, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setShowSuccess(true);
            setTimeout(() => {
                navigate(`/recruiters/${recruiterId}`);
            }, 2000);

        } catch (error) {
            console.error('Error:', error);
            alert('Erreur lors de la création de l\'offre');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-7rem)] lg:h-[calc(100vh-8rem)]">
            <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
                <div className="px-6 py-3">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate(`/recruiters/${recruiterId}`)}
                            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            <ArrowLeft className="h-5 w-5" />
                            <span className="text-sm font-medium">Retour</span>
                        </button>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white">Nouvelle Offre d'Emploi</h1>
                        <div className="w-24"></div>
                    </div>
                </div>
            </div>

            {showSuccess && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800 px-6 py-3 flex items-center gap-3">
                    <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                        Offre créée avec succès ! Redirection...
                    </span>
                </div>
            )}

            <div className="flex-1 overflow-y-auto min-h-0 bg-gray-50 dark:bg-gray-950">
                <form onSubmit={handleSubmit} className="max-w-5xl mx-auto p-6 space-y-6">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Briefcase className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                            Informations générales
                        </h2>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="lg:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Titre du poste *
                                </label>
                                <div className="relative">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={jobSearchTerm}
                                            onChange={handleJobSearchChange}
                                            onFocus={() => setShowJobDropdown(true)}
                                            placeholder="Rechercher un titre de poste..."
                                            className={`w-full px-4 py-2.5 pr-10 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${errors.title ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                        />
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                            <Search className="w-5 h-5 text-gray-400" />
                                        </div>
                                    </div>

                                    {selectedJob && (
                                        <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-950/30 text-primary-700 dark:text-primary-400 rounded-lg text-sm border border-primary-200 dark:border-primary-800">
                                            <Check className="w-4 h-4" />
                                            <span>{selectedJob.title}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedJob(null);
                                                    setJobSearchTerm('');
                                                    setFormData(prev => ({ ...prev, title: '' }));
                                                }}
                                                className="hover:text-primary-900 dark:hover:text-primary-200"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}

                                    {showJobDropdown && (jobSearchTerm.trim() || filteredJobs.length > 0) && !selectedJob && (
                                        <div className="absolute z-10 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                            {filteredJobs.length > 0 ? (
                                                <>
                                                    {filteredJobs.map(job => (
                                                        <button
                                                            key={job._id}
                                                            type="button"
                                                            onClick={() => handleSelectJob(job)}
                                                            className="w-full px-4 py-3 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 transition flex items-center justify-between group"
                                                        >
                                                            <span className="text-gray-900 dark:text-white">
                                                                {job.title}
                                                            </span>
                                                            <Plus className="w-4 h-4 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        </button>
                                                    ))}
                                                </>
                                            ) : jobSearchTerm.trim() ? (
                                                <div className="px-4 py-3 text-gray-500 dark:text-gray-400 text-center text-sm">
                                                    Aucun poste trouvé pour "{jobSearchTerm}"
                                                </div>
                                            ) : null}
                                        </div>
                                    )}

                                    {showJobDropdown && (
                                        <div
                                            className="fixed inset-0 z-0"
                                            onClick={() => setShowJobDropdown(false)}
                                        />
                                    )}
                                </div>
                                {errors.title && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {errors.title}
                                    </p>
                                )}
                            </div>

                            {!selectedJob && jobSearchTerm.trim() && (
                                <div className="lg:col-span-2 animate-fade-in">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Secteur d'activité (Nouveau poste) *
                                    </label>
                                    <select
                                        name="sector"
                                        value={formData.sector}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${errors.sector ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                    >
                                        <option value="">Sélectionnez un secteur</option>
                                        {availableSectors.map(sector => (
                                            <option key={sector._id} value={sector._id}>
                                                {sector.name_fr || sector.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.sector && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" />
                                            {errors.sector}
                                        </p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Ce poste n'existe pas encore dans notre base. Veuillez indiquer son secteur pour l'ajouter.
                                    </p>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Type de contrat *
                                </label>
                                <select
                                    name="contractType"
                                    value={formData.contractType}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${errors.contractType ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                >
                                    <option value="">Sélectionnez</option>
                                    {contractTypes.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                                {errors.contractType && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {errors.contractType}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Niveau d'expérience *
                                </label>
                                <select
                                    name="experienceLevel"
                                    value={formData.experienceLevel}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${errors.experienceLevel ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                >
                                    <option value="">Sélectionnez</option>
                                    {experienceLevels.map(level => (
                                        <option key={level.value} value={level.value}>{level.label}</option>
                                    ))}
                                </select>
                                {errors.experienceLevel && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {errors.experienceLevel}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Niveau d'éducation
                                </label>
                                <select
                                    name="educationLevel"
                                    value={formData.educationLevel}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                                >
                                    <option value="">Sélectionnez</option>
                                    {educationLevels.map(level => (
                                        <option key={level.value} value={level.value}>{level.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Image de l'offre
                                </label>
                                <div className={`relative border-2 border-dashed rounded-lg transition ${isDragOver
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                    : 'border-gray-300 dark:border-gray-600'
                                    }`}>
                                    {imagePreview ? (
                                        <div className="relative">
                                            <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                                            <button
                                                type="button"
                                                onClick={removeImage}
                                                className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div
                                            className="p-6 text-center cursor-pointer"
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <FileImage className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                                Cliquez ou glissez une image
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    name="image"
                                    accept="image/*"
                                    onChange={handleInputChange}
                                    className="hidden"
                                />
                            </div>
                        </div>

                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Description *
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={4}
                                placeholder="Décrivez le poste..."
                                className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition resize-none ${errors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                            />
                            {errors.description && (
                                <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    {errors.description}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <MapPin className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                            Localisation
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Adresse ligne 1
                                </label>
                                <input
                                    type="text"
                                    name="address.line1"
                                    value={formData.address.line1}
                                    onChange={handleInputChange}
                                    placeholder="123 Rue..."
                                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Adresse ligne 2
                                </label>
                                <input
                                    type="text"
                                    name="address.line2"
                                    value={formData.address.line2}
                                    onChange={handleInputChange}
                                    placeholder="Appartement, bureau..."
                                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Wilaya
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="address.wilaya"
                                        value={formData.address.wilaya}
                                        onChange={handleWilayaChange}
                                        onFocus={() => {
                                            if (formData.address.wilaya) {
                                                handleWilayaChange({ target: { value: formData.address.wilaya } });
                                            }
                                        }}
                                        placeholder="Rechercher une wilaya..."
                                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                                    />
                                    {showWilayaDropdown && wilayas.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                            {wilayas.map((wilaya) => (
                                                <button
                                                    key={wilaya.code}
                                                    type="button"
                                                    onClick={() => selectWilaya(wilaya)}
                                                    className="w-full px-4 py-2 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 transition flex items-center justify-between text-sm text-gray-900 dark:text-white"
                                                >
                                                    {wilaya.code} - {wilaya.name_fr || wilaya.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {showWilayaDropdown && (
                                        <div
                                            className="fixed inset-0 z-0"
                                            onClick={() => setShowWilayaDropdown(false)}
                                        />
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Ville *
                                </label>
                                <input
                                    type="text"
                                    list="communes-list"
                                    name="address.city"
                                    value={formData.address.city}
                                    onChange={handleCitySelect}
                                    placeholder="Sélectionnez ou tapez une ville"
                                    disabled={!formData.address.wilaya}
                                    className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${errors['address.city'] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                        } ${(!formData.address.wilaya) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                />
                                <datalist id="communes-list">
                                    {communes.map((commune) => (
                                        <option key={commune.id || Math.random()} value={commune.name} />
                                    ))}
                                </datalist>
                                {errors['address.city'] && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {errors['address.city']}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Code postal *
                                </label>
                                <input
                                    type="text"
                                    name="address.zipCode"
                                    value={formData.address.zipCode}
                                    onChange={handleInputChange}
                                    placeholder="Code postal"
                                    className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${errors['address.zipCode'] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                        }`}
                                />
                                {errors['address.zipCode'] && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {errors['address.zipCode']}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <GraduationCap className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                            Compétences requises
                        </h2>

                        {formData.required_skills.length > 0 && (
                            <div className="mb-4">
                                <div className="flex flex-wrap gap-2">
                                    {formData.required_skills.map(skillId => (
                                        <span
                                            key={skillId}
                                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 dark:bg-primary-950/30 text-primary-700 dark:text-primary-400 rounded-lg text-sm"
                                        >
                                            {getSkillName(skillId)}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSkill(skillId)}
                                                className="hover:text-primary-900 dark:hover:text-primary-200"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="relative">
                            <input
                                type="text"
                                value={skillSearchTerm}
                                onChange={(e) => {
                                    setSkillSearchTerm(e.target.value);
                                    setShowSkillDropdown(true);
                                }}
                                onFocus={() => setShowSkillDropdown(true)}
                                placeholder="Rechercher une compétence..."
                                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                            />

                            {showSkillDropdown && filteredSkills.length > 0 && (
                                <div className="absolute z-10 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                    {filteredSkills.map(skill => (
                                        <button
                                            key={skill._id}
                                            type="button"
                                            onClick={() => handleAddSkill(skill)}
                                            className="w-full px-4 py-2 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 transition flex items-center justify-between text-sm"
                                        >
                                            <span className="text-gray-900 dark:text-white">
                                                {skill.name_fr || skill.name}
                                            </span>
                                            <Plus className="w-4 h-4 text-primary-500" />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {showSkillDropdown && (
                                <div
                                    className="fixed inset-0 z-0"
                                    onClick={() => setShowSkillDropdown(false)}
                                />
                            )}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                            Dates
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Date de début
                                </label>
                                <input
                                    type="date"
                                    name="startAt"
                                    value={formData.startAt}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${errors.startAt ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                />
                                {errors.startAt && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {errors.startAt}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Date de fin
                                </label>
                                <input
                                    type="date"
                                    name="endAt"
                                    value={formData.endAt}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${errors.endAt ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                />
                                {errors.endAt && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {errors.endAt}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tags</h2>

                        {formData.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                                {formData.tags.map(tag => (
                                    <span
                                        key={tag}
                                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm"
                                    >
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(tag)}
                                            className="hover:text-blue-900 dark:hover:text-blue-200"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                placeholder="Ajouter un tag"
                                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                            />
                            <button
                                type="button"
                                onClick={handleAddTag}
                                className="px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition"
                            >
                                <Plus className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Options</h2>

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                id="isFeatured"
                                name="isFeatured"
                                checked={formData.isFeatured}
                                onChange={handleInputChange}
                                className="w-5 h-5 rounded text-yellow-500 focus:ring-yellow-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                <Star className="w-4 h-4 text-yellow-500" />
                                Offre mise en avant
                            </span>
                        </label>
                    </div>

                </form>
            </div>

            <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-4 flex justify-end gap-3 flex-shrink-0 rounded-b-xl">
                <button
                    type="button"
                    onClick={() => navigate(`/recruiters/${recruiterId}`)}
                    className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition font-medium"
                >
                    Annuler
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition font-medium flex items-center gap-2 disabled:opacity-50"
                >
                    {isSubmitting ? (
                        <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Création...
                        </>
                    ) : (
                        <>
                            <Save className="w-5 h-5" />
                            Créer l'offre
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default NewJobOffer;
